import React, { FC } from 'react';
import { BasicContainer, BodyText, FlexCol, FlexRow, H2Text } from '@minecraft.atoms';
import { CardCvvElement, CardMonthElement, CardNumberElement, CardYearElement } from '@recurly/react-recurly';
import { useTranslation } from '@blocs.i18n';
import Icon from '@minecraft.icon';
import { TextInput } from '@minecraft.molecules';
import { StyledRecurlyInput } from './fields/StyledRecurlyInput';

export const NameFields: FC<{ firstNamePlaceholder?: string; lastNamePlaceholder?: string }> = ({
  firstNamePlaceholder,
  lastNamePlaceholder,
}) => {
  const { t } = useTranslation();

  return (
    <FlexRow gap="4" wrap="nowrap" alignItems="center" mdProps={{ w: 'full' }}>
      <TextInput
        name="first_name"
        label={t('common:form.label.firstName')}
        required
        inputProps={{
          'data-recurly': 'first_name',
          'data-qa-id': 'cc-firstname-container',
          placeholder: firstNamePlaceholder,
        }}
      />
      <TextInput
        name="last_name"
        label={t('common:form.label.lastName')}
        required
        inputProps={{
          'data-recurly': 'last_name',
          'data-qa-id': 'cc-lastname-container',
          placeholder: lastNamePlaceholder,
        }}
      />
    </FlexRow>
  );
};

export const CardNumberField: FC<{ placeholder?: string }> = ({ placeholder }) => {
  const { t } = useTranslation();

  return (
    <FlexRow data-qa-id="cc-number-container" gap="4" wrap="nowrap" alignItems="center" w="full">
      <StyledRecurlyInput
        name="number"
        label={t('common:form.label.cardNumber')}
        required
        RecurlyInput={CardNumberElement}
        placeholder={placeholder}
        inputProps={{ className: 'cn_w-full' }}
        LabelEndSlotComponent={
          <FlexRow gap="2" wrap="nowrap">
            <Icon name="master-card" size="lg" />
            <Icon name="visa" size="lg" />
            <Icon name="amex" size="lg" />
          </FlexRow>
        }
      />
    </FlexRow>
  );
};

export const ExpirationField: FC<{ monthPlaceholder?: string; yearPlaceholder?: string }> = ({
  monthPlaceholder,
  yearPlaceholder,
}) => {
  const { t } = useTranslation();

  return (
    <FlexRow data-qa-id="cc-expiration-container" gap="1" w="1/2" mdProps={{ w: 'full' }}>
      <BodyText as="label" fontWeight="semibold" htmlFor="month" noWrap>
        {t('common:form.label.expirationDate')} *
      </BodyText>
      <FlexRow gap="2" wrap="nowrap" alignItems="center">
        <FlexCol gap="1">
          <StyledRecurlyInput
            inputProps={{ className: 'cn_w-full', dataQaId: 'cc-month-expiration-container' }}
            name="month"
            label={t('common:form.label.month')}
            placeholder={monthPlaceholder ?? t('common:form.placeholder.expMonth')}
            hideLabel
            required
            RecurlyInput={CardMonthElement}
          />
        </FlexCol>
        <BodyText as="span">/</BodyText>
        <FlexCol gap="1">
          <StyledRecurlyInput
            inputProps={{ className: 'cn_w-full', dataQaId: 'cc-year-expiration-container' }}
            name="year"
            label={t('common:form.label.year')}
            placeholder={yearPlaceholder ?? t('common:form.placeholder.expYear2')}
            hideLabel
            required
            RecurlyInput={CardYearElement}
          />
        </FlexCol>
      </FlexRow>
    </FlexRow>
  );
};

export const CvvField: FC = () => {
  const { t } = useTranslation();

  return (
    <FlexCol gap="1" w="1/2" mdProps={{ w: 'full' }}>
      <StyledRecurlyInput
        name="cvv"
        label={t('common:form.label.securityCode')}
        inputProps={{ className: 'cn_w-full', dataQaId: 'cc-cvv-container' }}
        placeholder={t('common:form.label.cvv')}
        required
        RecurlyInput={CardCvvElement}
      />
    </FlexCol>
  );
};

// Legacy (WEB_PAYWALL_BILLING_ADDRESS off) layout: separate visible Month*/Year* fields, and
// CVV*/Zip* side by side — matches the pre-billing-address design exactly.
const LegacyExpirationFields: FC<{
  monthPlaceholder?: string;
  yearPlaceholder?: string;
}> = ({ monthPlaceholder, yearPlaceholder }) => {
  const { t } = useTranslation();

  return (
    <FlexRow data-qa-id="cc-expiration-container" gap="4" wrap="nowrap">
      <StyledRecurlyInput
        inputProps={{ dataQaId: 'cc-month-expiration-container' }}
        name="month"
        label={t('common:form.label.month')}
        required
        RecurlyInput={CardMonthElement}
        placeholder={monthPlaceholder}
      />
      <StyledRecurlyInput
        inputProps={{ dataQaId: 'cc-year-expiration-container' }}
        name="year"
        label={t('common:form.label.year')}
        required
        RecurlyInput={CardYearElement}
        placeholder={yearPlaceholder}
      />
    </FlexRow>
  );
};

const LegacyCvvZipFields: FC<{ showCvvIcons?: boolean }> = ({ showCvvIcons }) => {
  const { t } = useTranslation();

  return (
    <FlexRow gap="4" wrap="nowrap" alignItems="center" w="full">
      <BasicContainer w={showCvvIcons ? '2/3' : '1/2'}>
        <StyledRecurlyInput
          name="cvv"
          label={t('common:form.label.cvv')}
          inputProps={{ className: 'cn_w-full', dataQaId: 'cc-cvv-container' }}
          required
          RecurlyInput={CardCvvElement}
          EndSlotComponent={
            showCvvIcons && (
              <FlexRow wrap="nowrap">
                <Icon name="amex-cvv" size="xl" />
                <Icon name="visa-mc-cvv" size="xl" />
              </FlexRow>
            )
          }
        />
      </BasicContainer>
      <BasicContainer w={showCvvIcons ? '1/3' : '1/2'}>
        <TextInput
          name="postal_code"
          label={t('common:paywall.checkout.billingAddress.zipCode')}
          inputProps={{ 'data-recurly': 'postal_code', 'data-qa-id': 'cc-zipcode-container' }}
          required
        />
      </BasicContainer>
    </FlexRow>
  );
};

export const CreditCardDetailsFormLayout: FC<{
  isMobile: boolean;
  isBillingAddressFieldEnabled?: boolean;
  firstNamePlaceholder?: string;
  lastNamePlaceholder?: string;
  cardNumberPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
}> = ({
  isMobile,
  isBillingAddressFieldEnabled,
  firstNamePlaceholder,
  lastNamePlaceholder,
  cardNumberPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {isBillingAddressFieldEnabled && <H2Text>{t('common:paywall.checkout.creditCard.title')}</H2Text>}
      <NameFields firstNamePlaceholder={firstNamePlaceholder} lastNamePlaceholder={lastNamePlaceholder} />
      <CardNumberField placeholder={cardNumberPlaceholder} />
      {isBillingAddressFieldEnabled ? (
        <FlexRow gap="4" wrap="nowrap" w="full">
          <ExpirationField monthPlaceholder={monthPlaceholder} yearPlaceholder={yearPlaceholder} />
          <CvvField />
        </FlexRow>
      ) : (
        <>
          <LegacyExpirationFields monthPlaceholder={monthPlaceholder} yearPlaceholder={yearPlaceholder} />
          <LegacyCvvZipFields showCvvIcons={!isMobile} />
        </>
      )}
    </>
  );
};
